import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useT } from "../../context/LanguageContext";
import { toast } from "sonner";
import QuickReplyChips from "../components/QuickReplyChips";
import { ChevronLeft, Send, Loader2, MessageSquareOff } from "lucide-react";

/**
 * V2 / Mobile-native DM thread.
 * Renders inside <MobileShell> at /v2/chat/:peerId so users keep the dark
 * mobile chrome and bottom nav while messaging an opponent.
 *
 * Reuses unchanged endpoints:
 *   GET /dm/threads          → resolve peer username + whatsapp
 *   GET /dm/{peerId}/messages?since=ISO  → polled every 5s
 *   POST /dm/{peerId}/messages           → send a new message
 */
export default function DMThreadV2() {
  const { peerId } = useParams();
  const { user } = useAuth();
  const { t } = useT();
  const nav = useNavigate();

  const [messages, setMessages] = useState([]);
  const [peer, setPeer] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const endRef = useRef(null);
  const sinceRef = useRef(null);

  const scrollToBottom = () => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  };

  const fetchMessages = useCallback(async (initial = false) => {
    try {
      const params = sinceRef.current ? { since: sinceRef.current } : {};
      const { data } = await api.get(`/dm/${peerId}/messages`, { params });
      if (data.length) {
        sinceRef.current = data[data.length - 1].created_at;
        setMessages((prev) => (initial ? data : [...prev, ...data]));
        setTimeout(scrollToBottom, 50);
      } else if (initial) {
        setMessages([]);
      }
    } catch (e) { /* noop */ }
    finally { if (initial) setLoading(false); }
  }, [peerId]);

  // Resolve peer info from threads list (cheap, avoids extra endpoint)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/dm/threads");
        if (!alive) return;
        const thread = data.find((x) => x.user.id === peerId);
        if (thread) setPeer(thread.user);
      } catch (e) { /* noop */ }
    })();
    return () => { alive = false; };
  }, [peerId]);

  useEffect(() => {
    sinceRef.current = null;
    setMessages([]);
    setLoading(true);
    fetchMessages(true);
    const iv = setInterval(() => fetchMessages(false), 5000);
    return () => clearInterval(iv);
  }, [fetchMessages]);

  const sendText = async (txt) => {
    if (!txt || !txt.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/dm/${peerId}/messages`, { text: txt.trim() });
      setMessages((prev) => [...prev, data]);
      sinceRef.current = data.created_at;
      setTimeout(scrollToBottom, 50);
    } catch (err) {
      toast.error(formatApiError(err));
      throw err;
    } finally {
      setSending(false);
    }
  };

  const onSend = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      await sendText(text);
      setText("");
    } catch { /* error already toasted */ }
  };

  const peerName = peer?.username || "…";
  const initial = (peerName || "?").charAt(0).toUpperCase();
  const waNumber = peer?.whatsapp ? peer.whatsapp.replace(/[^0-9]/g, "") : null;

  return (
    <div className="flex flex-col h-[calc(100vh-7rem)]" data-testid="v2-dm-thread">
      {/* Header */}
      <div className="px-5 pt-5 pb-3 flex items-center gap-3" data-testid="v2-dm-header">
        <button
          type="button"
          onClick={() => nav(-1)}
          data-testid="v2-dm-back"
          className="text-[#A1A1AA] hover:text-white p-1 -ml-1"
          aria-label={t("v2.match.back")}
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        <button
          type="button"
          onClick={() => peer && nav(`/v2/players/${encodeURIComponent(peer.username)}`)}
          className="flex items-center gap-3 flex-1 min-w-0 text-left"
          data-testid="v2-dm-peer"
        >
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#3B82F6] flex items-center justify-center font-bold text-base text-white flex-shrink-0">
            {initial}
          </div>
          <div className="min-w-0">
            <div className="text-base font-bold text-white truncate">{peerName}</div>
            {peer?.game_id && (
              <div className="text-[10px] font-mono text-[#71717A] uppercase tracking-widest truncate">{peer.game_id}</div>
            )}
          </div>
        </button>

        {waNumber && (
          <a
            href={`https://wa.me/${waNumber}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="v2-dm-whatsapp"
            className="flex-shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#25D366]/15 border border-[#25D366]/40 text-[#25D366] text-[10px] font-mono uppercase tracking-widest hover:bg-[#25D366]/25 transition-colors"
          >
            WA
          </a>
        )}
      </div>

      {/* Messages list */}
      <div className="flex-1 min-h-0 overflow-y-auto px-5 py-3 space-y-2 bg-[#0A0A14]" data-testid="v2-dm-list">
        {loading ? (
          <div className="flex items-center justify-center pt-10 text-[#52525B]" data-testid="v2-dm-loading">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center pt-16 text-center text-[#71717A]" data-testid="v2-dm-empty">
            <MessageSquareOff className="w-8 h-8 mb-2" />
            <div className="text-sm font-bold text-white">{t("v2.dm.empty.title")}</div>
            <p className="text-xs mt-2 max-w-xs">{t("v2.dm.empty.body")}</p>
          </div>
        ) : (
          messages.map((m) => {
            const mine = m.from_id === user?.id;
            return (
              <div
                key={m.id}
                className={`flex ${mine ? "justify-end" : "justify-start"}`}
                data-testid={`v2-dm-msg-${m.id}`}
              >
                <div className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-sm shadow-sm ${
                  mine
                    ? "bg-gradient-to-br from-[#7C3AED] to-[#6D28D9] text-white"
                    : "bg-[#12121C] border border-[#1F1F2A] text-white"
                }`}>
                  <div className="whitespace-pre-wrap break-words">{m.text}</div>
                  <div className={`text-[9px] font-mono mt-1 ${mine ? "text-white/60" : "text-[#71717A]"}`}>
                    {fmtTs(m.created_at)}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={endRef} />
      </div>

      {/* Composer */}
      <form
        onSubmit={onSend}
        className="px-3 pt-2 pb-3 border-t border-[#1F1F2A] bg-[#070710] space-y-2"
        data-testid="v2-dm-form"
      >
        <QuickReplyChips
          onSend={sendText}
          disabled={sending}
          testIdPrefix="v2-dm-quick"
        />
        <div className="flex gap-2">
          <input
            data-testid="v2-dm-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t("v2.dm.placeholder")}
            maxLength={2000}
            dir="auto"
            className="flex-1 rounded-full bg-[#12121C] border border-[#1F1F2A] focus:border-[#7C3AED] focus:outline-none px-4 py-2.5 text-sm text-white placeholder-[#52525B] transition-colors"
          />
          <button
            type="submit"
            disabled={sending || !text.trim()}
            data-testid="v2-dm-send"
            className="flex-shrink-0 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#3B82F6] text-white p-2.5 shadow-lg shadow-[#7C3AED]/30 hover:opacity-95 disabled:opacity-50 transition-opacity"
            aria-label={t("v2.dm.send")}
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </form>
    </div>
  );
}

function fmtTs(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}
